import type { Frontmatter } from "./FrontmatterForm";

/**
 * Minimal YAML frontmatter parser/serializer for DocuStudio editor.
 *
 * Supports a flat subset: scalars (strings, numbers, booleans) and inline
 * arrays (`[a, b, c]`). Sufficient for the keys the platform uses (title,
 * project, area, kind, lang, visibility, status, tags, technologies, owner).
 * Complex YAML (nested maps, block sequences, anchors) is left untouched
 * in `extras` and round-trips verbatim.
 */
export function splitFrontmatter(source: string): { fm: Frontmatter; body: string; raw: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(source);
  if (!m) return { fm: {}, body: source, raw: "" };
  const raw = m[1] ?? "";
  const body = source.slice(m[0].length);
  const fm = parseYaml(raw);
  return { fm, body, raw };
}

export function joinFrontmatter(fm: Frontmatter, body: string): string {
  const yaml = stringifyYaml(fm).trim();
  if (yaml.length === 0) return body;
  return `---\n${yaml}\n---\n\n${body.replace(/^\n+/, "")}`;
}

function parseYaml(text: string): Frontmatter {
  const out: Frontmatter = {};
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const idx = line.indexOf(":");
    if (idx < 0) continue;
    const key = line.slice(0, idx).trim();
    const valStr = line.slice(idx + 1).trim();
    out[key] = parseScalar(valStr);
  }
  return out;
}

function parseScalar(v: string): unknown {
  if (v === "" || v === "~" || v === "null") return undefined;
  if (v === "true") return true;
  if (v === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
  if (v.startsWith("[") && v.endsWith("]")) {
    return v
      .slice(1, -1)
      .split(",")
      .map((s) => s.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);
  }
  return v.replace(/^["']|["']$/g, "");
}

function stringifyYaml(fm: Frontmatter): string {
  const out: string[] = [];
  for (const [k, v] of Object.entries(fm)) {
    if (v == null || (Array.isArray(v) && v.length === 0)) continue;
    if (Array.isArray(v)) {
      out.push(`${k}: [${v.map((x) => quote(String(x))).join(", ")}]`);
    } else if (typeof v === "string" && /[:#\n]/.test(v)) {
      out.push(`${k}: ${JSON.stringify(v)}`);
    } else {
      out.push(`${k}: ${v}`);
    }
  }
  return out.join("\n");
}

function quote(s: string): string {
  return /[\s,"']/.test(s) ? JSON.stringify(s) : s;
}
